// lib/leasing/unitOnVenue.ts
//
// WHERE ONE OF OUR UNITS IS ON THE CENTRE'S MAP (owner, 2026-10-13: "when we select a unit we should zoom
// in on the map"). Our units are outlined on the floor's own drawing, in fractions of the image; the map's
// shops are in longitude and latitude, and the two share no code (Espace Saint Quentin: 22 of 133 shops
// carry the landlord's code, ours read 0-1, 1-1). The leased units bridge them: the same brand on both
// sides gives a pair of points, and the pairs fit the floor drawing onto the map (an affine transform,
// least squares, the pairs that disagree most dropped once). A leased unit is found by its brand first;
// any unit, vacant ones included, by its outline's centre carried onto the map.
import { namesBrand } from '#lib/leasing/storeQuery';
import { readPolygon, type PlanPoint } from '#lib/leasing/planGeometry';

type Lnglat = [number, number];
export type VenueShop = { id: string; name: string | null; rings: Array<Array<Lnglat>> };
export type DrawnUnit = { id: string; tenantName: string | null; geoJson: unknown };

const centre = (points: Array<{ x: number; y: number }>) => ({ x: points.reduce((s, p) => s + p.x, 0) / points.length, y: points.reduce((s, p) => s + p.y, 0) / points.length });
const shopCentre = (shop: VenueShop): Lnglat => {
  const ring = shop.rings.reduce((a, b) => (b.length > a.length ? b : a), [] as Lnglat[]);
  return [ring.reduce((s, p) => s + p[0], 0) / ring.length, ring.reduce((s, p) => s + p[1], 0) / ring.length];
};
const sameBrand = (a: string, b: string) => namesBrand(a, b) || namesBrand(b, a);

/** Solves the 3x3 system m.v = r (Cramer). Null when the points are collinear. */
const solve3 = (m: number[][], r: number[]): number[] | null => {
  const det = (a: number[][]) => a[0]![0]! * (a[1]![1]! * a[2]![2]! - a[1]![2]! * a[2]![1]!) - a[0]![1]! * (a[1]![0]! * a[2]![2]! - a[1]![2]! * a[2]![0]!) + a[0]![2]! * (a[1]![0]! * a[2]![1]! - a[1]![1]! * a[2]![0]!);
  const d = det(m);
  if (Math.abs(d) < 1e-12) return null;
  return [0, 1, 2].map((col) => det(m.map((row, i) => row.map((v, j) => (j === col ? r[i]! : v)))) / d);
};

/** The affine map from drawing fractions to longitude and latitude, fitted on the pairs. */
export function fitDrawingToMap(pairs: Array<{ at: PlanPoint; to: Lnglat }>): ((p: PlanPoint) => Lnglat) | null {
  const fit = (use: typeof pairs) => {
    if (use.length < 3) return null;
    const m = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    const rx = [0, 0, 0], ry = [0, 0, 0];
    for (const { at, to } of use) {
      const v = [at.x, at.y, 1];
      for (let i = 0; i < 3; i++) { for (let j = 0; j < 3; j++) m[i]![j]! += v[i]! * v[j]!; rx[i]! += v[i]! * to[0]; ry[i]! += v[i]! * to[1]; }
    }
    const a = solve3(m, rx), b = solve3(m, ry);
    return a && b ? (p: PlanPoint): Lnglat => [a[0]! * p.x + a[1]! * p.y + a[2]!, b[0]! * p.x + b[1]! * p.y + b[2]!] : null;
  };
  const first = fit(pairs);
  if (!first || pairs.length < 5) return first;
  // A brand with two shops, or a name read wrong, pairs two places that are not one: the worst third of
  // the pairs, by distance to the first fit, are dropped before fitting again.
  const errors = pairs.map((pair) => { const [x, y] = first(pair.at); return Math.hypot(x - pair.to[0], y - pair.to[1]); });
  const cut = [...errors].sort((a, b) => a - b)[Math.floor(pairs.length * 0.67)]!;
  return fit(pairs.filter((_, i) => errors[i]! <= cut)) ?? first;
}

const inside = ([x, y]: Lnglat, ring: Lnglat[]) => {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!, [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
};
const metres = (a: Lnglat, b: Lnglat) => Math.hypot((a[0] - b[0]) * 111320 * Math.cos((a[1] * Math.PI) / 180), (a[1] - b[1]) * 110540);

/** The map's shop that is this unit, or null when neither its brand nor the fitted drawing can place it. */
export function locateUnitOnVenue(unitId: string, units: DrawnUnit[], shops: VenueShop[]): { shopId: string; how: 'brand' | 'drawing' } | null {
  const unit = units.find((u) => u.id === unitId);
  if (!unit) return null;
  const named = shops.filter((s) => s.name);
  const own = unit.tenantName ? named.filter((s) => sameBrand(unit.tenantName!, s.name!)) : [];
  if (own.length === 1) return { shopId: own[0]!.id, how: 'brand' };
  const outline = readPolygon(unit.geoJson);
  if (outline.length < 3) return null;
  const pairs = units.flatMap((u) => {
    const points = readPolygon(u.geoJson);
    const same = u.tenantName && points.length >= 3 ? named.filter((s) => sameBrand(u.tenantName!, s.name!)) : [];
    return same.length === 1 ? [{ at: centre(points), to: shopCentre(same[0]!) }] : [];
  });
  const toMap = fitDrawingToMap(pairs);
  if (!toMap) return null;
  const at = toMap(centre(outline));
  // A brand with several shops on the level: the one where the drawing puts this unit.
  if (own.length > 1) return { shopId: own.map((s) => ({ s, d: metres(at, shopCentre(s)) })).sort((x, y) => x.d - y.d)[0]!.s.id, how: 'brand' };
  const hit = shops.find((s) => s.rings.some((ring) => inside(at, ring)));
  if (hit) return { shopId: hit.id, how: 'drawing' };
  // Just off every outline (a corridor between two shops): the nearest shop within 25 m.
  const nearest = shops.map((s) => ({ s, d: metres(at, shopCentre(s)) })).sort((a, b) => a.d - b.d)[0];
  return nearest && nearest.d <= 25 ? { shopId: nearest.s.id, how: 'drawing' } : null;
}

/**
 * The map's level a floor of ours is, by the brands they share: the numbering differs (Espace Saint
 * Quentin's "RDC" is level -1 in our records and 0 on the map, where -1 is the car park).
 */
export function venueLevelForFloor(units: DrawnUnit[], shops: Array<VenueShop & { level: string | null }>): string | null {
  const counts = new Map<string, number>();
  for (const u of units) {
    if (!u.tenantName) continue;
    for (const s of shops) if (s.level && s.name && sameBrand(u.tenantName, s.name)) counts.set(s.level, (counts.get(s.level) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}
